// --------------------------------------------------------------------------------
// Enums
// --------------------------------------------------------------------------------

/**
 * @example
 * ```graphql
 * enum EntityQueryOperator {
 *   BETWEEN
 *   CONTAINS
 *   ENDS_WITH
 *   EQUAL
 *   GREATER_THAN
 *   GREATER_THAN_OR_EQUAL
 *   IN
 *   IS_NOT_NULL
 *   IS_NULL
 *   LIKE
 *   NOT_BETWEEN
 *   NOT_EQUAL
 *   NOT_IN
 *   NOT_LIKE
 *   REGEXP
 *   SMALLER_THAN
 *   SMALLER_THAN_OR_EQUAL
 *   STARTS_WITH
 * }
 * ```
 */
export const EntityQueryOperator = Object.freeze({
  BETWEEN: 'BETWEEN',
  CONTAINS: 'CONTAINS',
  ENDS_WITH: 'ENDS_WITH',
  EQUAL: 'EQUAL',
  GREATER_THAN: 'GREATER_THAN',
  GREATER_THAN_OR_EQUAL: 'GREATER_THAN_OR_EQUAL',
  IN: 'IN',
  IS_NOT_NULL: 'IS_NOT_NULL',
  IS_NULL: 'IS_NULL',
  LIKE: 'LIKE',
  NOT_BETWEEN: 'NOT_BETWEEN',
  NOT_EQUAL: 'NOT_EQUAL',
  NOT_IN: 'NOT_IN',
  NOT_LIKE: 'NOT_LIKE',
  REGEXP: 'REGEXP',
  SMALLER_THAN: 'SMALLER_THAN',
  SMALLER_THAN_OR_EQUAL: 'SMALLER_THAN_OR_EQUAL',
  STARTS_WITH: 'STARTS_WITH'
});

/**
 * @example
 * ```graphql
 * enum EntityType {
 *   """Kaufabwicklung"""
 *   COMMERCE_CHECKOUT_FLOW
 * 
 *   """Währung"""
 *   COMMERCE_CURRENCY
 * 
 *   """Lizenz"""
 *   COMMERCE_LICENSE
 * 
 *   """Bestellung"""
 *   COMMERCE_ORDER
 * 
 *   """Bestellartikel"""
 *   COMMERCE_ORDER_ITEM
 * 
 *   """Bestellartikeltyp"""
 *   COMMERCE_ORDER_ITEM_TYPE
 * 
 *   """Bestelltyp"""
 *   COMMERCE_ORDER_TYPE
 * 
 *   """Payment"""
 *   COMMERCE_PAYMENT
 * 
 *   """Payment gateway"""
 *   COMMERCE_PAYMENT_GATEWAY
 * 
 *   """Zahlungsmethode"""
 *   COMMERCE_PAYMENT_METHOD
 * 
 *   """Product"""
 *   COMMERCE_PRODUCT
 * 
 *   """Produkteigenschaft"""
 *   COMMERCE_PRODUCT_ATTRIBUTE
 * 
 *   """Wert des Produktattributs"""
 *   COMMERCE_PRODUCT_ATTRIBUTE_VALUE
 * 
 *   """Produkttyp"""
 *   COMMERCE_PRODUCT_TYPE
 * 
 *   """Produktvarianten"""
 *   COMMERCE_PRODUCT_VARIATION
 * 
 *   """Aktion"""
 *   COMMERCE_PROMOTION
 * 
 *   """Kupon"""
 *   COMMERCE_PROMOTION_COUPON
 * 
 *   """Shipment"""
 *   COMMERCE_SHIPMENT
 * 
 *   """Shipment type"""
 *   COMMERCE_SHIPMENT_TYPE
 * 
 *   """Versandart"""
 *   COMMERCE_SHIPPING_METHOD
 * 
 *   """Shop"""
 *   COMMERCE_STORE
 * 
 *   """Language"""
 *   CONFIGURABLE_LANGUAGE
 * 
 *   """Feld"""
 *   FIELD_CONFIG
 * 
 *   """Feldspeicher"""
 *   FIELD_STORAGE_CONFIG
 * 
 *   """Datei"""
 *   FILE
 * 
 *   """Media"""
 *   MEDIA
 * 
 *   """Menü"""
 *   MENU
 * 
 *   """Benutzerdefinierter Menülink"""
 *   MENU_LINK_CONTENT
 * 
 *   """Content"""
 *   NODE
 * 
 *   """Paragraph"""
 *   PARAGRAPH
 * 
 *   """Paragraphs Blokkli Edit State"""
 *   PARAGRAPHS_BLOKKLI_EDIT_STATE
 * 
 *   """Seitenabschnittsbibliothekseintrag"""
 *   PARAGRAPHS_LIBRARY_ITEM
 * 
 *   """Seitenabschnittstyp"""
 *   PARAGRAPHS_TYPE
 * 
 *   """Password Policy"""
 *   PASSWORD_POLICY
 * 
 *   """Pharmacy"""
 *   PHARMACY
 * 
 *   """Portfolio Type Category Combination"""
 *   PHS_PORTFOLIO_TYPE_CATEGORY
 * 
 *   """Profil"""
 *   PROFILE
 * 
 *   """Umleiten"""
 *   REDIRECT
 * 
 *   """Registration"""
 *   REGISTRATION
 * 
 *   """Registration settings"""
 *   REGISTRATION_SETTINGS
 * 
 *   """Rokka Metadata"""
 *   ROKKA_METADATA
 * 
 *   """Taxonomy term"""
 *   TAXONOMY_TERM
 * 
 *   """Translatable config pages"""
 *   TRANSLATABLE_CONFIG_PAGES
 * 
 *   """User"""
 *   USER
 * 
 *   """Rolle"""
 *   USER_ROLE
 * 
 *   """Ansicht"""
 *   VIEW
 * 
 *   """Webform"""
 *   WEBFORM
 * }
 * ```
 */
export const EntityType = Object.freeze({
  /** Kaufabwicklung */
  COMMERCE_CHECKOUT_FLOW: 'COMMERCE_CHECKOUT_FLOW',
  /** Währung */
  COMMERCE_CURRENCY: 'COMMERCE_CURRENCY',
  /** Lizenz */
  COMMERCE_LICENSE: 'COMMERCE_LICENSE',
  /** Bestellung */
  COMMERCE_ORDER: 'COMMERCE_ORDER',
  /** Bestellartikel */
  COMMERCE_ORDER_ITEM: 'COMMERCE_ORDER_ITEM',
  /** Bestellartikeltyp */
  COMMERCE_ORDER_ITEM_TYPE: 'COMMERCE_ORDER_ITEM_TYPE',
  /** Bestelltyp */
  COMMERCE_ORDER_TYPE: 'COMMERCE_ORDER_TYPE',
  /** Payment */
  COMMERCE_PAYMENT: 'COMMERCE_PAYMENT',
  /** Payment gateway */
  COMMERCE_PAYMENT_GATEWAY: 'COMMERCE_PAYMENT_GATEWAY',
  /** Zahlungsmethode */
  COMMERCE_PAYMENT_METHOD: 'COMMERCE_PAYMENT_METHOD',
  /** Product */
  COMMERCE_PRODUCT: 'COMMERCE_PRODUCT',
  /** Produkteigenschaft */
  COMMERCE_PRODUCT_ATTRIBUTE: 'COMMERCE_PRODUCT_ATTRIBUTE',
  /** Wert des Produktattributs */
  COMMERCE_PRODUCT_ATTRIBUTE_VALUE: 'COMMERCE_PRODUCT_ATTRIBUTE_VALUE',
  /** Produkttyp */
  COMMERCE_PRODUCT_TYPE: 'COMMERCE_PRODUCT_TYPE',
  /** Produktvarianten */
  COMMERCE_PRODUCT_VARIATION: 'COMMERCE_PRODUCT_VARIATION',
  /** Aktion */
  COMMERCE_PROMOTION: 'COMMERCE_PROMOTION',
  /** Kupon */
  COMMERCE_PROMOTION_COUPON: 'COMMERCE_PROMOTION_COUPON',
  /** Shipment */
  COMMERCE_SHIPMENT: 'COMMERCE_SHIPMENT',
  /** Shipment type */
  COMMERCE_SHIPMENT_TYPE: 'COMMERCE_SHIPMENT_TYPE',
  /** Versandart */
  COMMERCE_SHIPPING_METHOD: 'COMMERCE_SHIPPING_METHOD',
  /** Shop */
  COMMERCE_STORE: 'COMMERCE_STORE',
  /** Language */
  CONFIGURABLE_LANGUAGE: 'CONFIGURABLE_LANGUAGE',
  /** Feld */
  FIELD_CONFIG: 'FIELD_CONFIG',
  /** Feldspeicher */
  FIELD_STORAGE_CONFIG: 'FIELD_STORAGE_CONFIG',
  /** Datei */
  FILE: 'FILE',
  /** Media */
  MEDIA: 'MEDIA',
  /** Menü */
  MENU: 'MENU',
  /** Benutzerdefinierter Menülink */
  MENU_LINK_CONTENT: 'MENU_LINK_CONTENT',
  /** Content */
  NODE: 'NODE',
  /** Paragraph */
  PARAGRAPH: 'PARAGRAPH',
  /** Paragraphs Blokkli Edit State */
  PARAGRAPHS_BLOKKLI_EDIT_STATE: 'PARAGRAPHS_BLOKKLI_EDIT_STATE',
  /** Seitenabschnittsbibliothekseintrag */
  PARAGRAPHS_LIBRARY_ITEM: 'PARAGRAPHS_LIBRARY_ITEM',
  /** Seitenabschnittstyp */
  PARAGRAPHS_TYPE: 'PARAGRAPHS_TYPE',
  /** Password Policy */
  PASSWORD_POLICY: 'PASSWORD_POLICY',
  /** Pharmacy */
  PHARMACY: 'PHARMACY',
  /** Portfolio Type Category Combination */
  PHS_PORTFOLIO_TYPE_CATEGORY: 'PHS_PORTFOLIO_TYPE_CATEGORY',
  /** Profil */
  PROFILE: 'PROFILE',
  /** Umleiten */
  REDIRECT: 'REDIRECT',
  /** Registration */
  REGISTRATION: 'REGISTRATION',
  /** Registration settings */
  REGISTRATION_SETTINGS: 'REGISTRATION_SETTINGS',
  /** Rokka Metadata */
  ROKKA_METADATA: 'ROKKA_METADATA',
  /** Taxonomy term */
  TAXONOMY_TERM: 'TAXONOMY_TERM',
  /** Translatable config pages */
  TRANSLATABLE_CONFIG_PAGES: 'TRANSLATABLE_CONFIG_PAGES',
  /** User */
  USER: 'USER',
  /** Rolle */
  USER_ROLE: 'USER_ROLE',
  /** Ansicht */
  VIEW: 'VIEW',
  /** Webform */
  WEBFORM: 'WEBFORM'
});

/**
 * @example
 * ```graphql
 * enum Langcode {
 *   """German"""
 *   DE
 * 
 *   """French"""
 *   FR
 * 
 *   """Italian"""
 *   IT
 * }
 * ```
 */
export const Langcode = Object.freeze({
  /** German */
  DE: 'DE',
  /** French */
  FR: 'FR',
  /** Italian */
  IT: 'IT'
});

/**
 * @example
 * ```graphql
 * enum ParagraphsBlokkliEditableFieldConfigType {
 *   frame
 *   plain
 * }
 * ```
 */
export const ParagraphsBlokkliEditableFieldConfigType = Object.freeze({
  frame: 'frame',
  plain: 'plain'
});

/**
 * @example
 * ```graphql
 * enum ParagraphsBlokkliRemoteVideoProvider {
 *   VIMEO
 *   YOUTUBE
 * }
 * ```
 */
export const ParagraphsBlokkliRemoteVideoProvider = Object.freeze({
  VIMEO: 'VIMEO',
  YOUTUBE: 'YOUTUBE'
});

/**
 * @example
 * ```graphql
 * enum SiteColor {
 *   BERRY
 *   BLUE
 *   BLUE_GRADIENT
 *   CYAN
 *   CYAN_LIGHT
 *   CYAN_LIGHTER
 *   GREEN
 *   GREEN_ALT
 *   LIME
 *   LIME_LIGHT
 *   ORANGE
 *   PINK
 * }
 * ```
 */
export const SiteColor = Object.freeze({
  BERRY: 'BERRY',
  BLUE: 'BLUE',
  BLUE_GRADIENT: 'BLUE_GRADIENT',
  CYAN: 'CYAN',
  CYAN_LIGHT: 'CYAN_LIGHT',
  CYAN_LIGHTER: 'CYAN_LIGHTER',
  GREEN: 'GREEN',
  GREEN_ALT: 'GREEN_ALT',
  LIME: 'LIME',
  LIME_LIGHT: 'LIME_LIGHT',
  ORANGE: 'ORANGE',
  PINK: 'PINK'
});

/**
 * The result of the user signup check.
 * 
 * @example
 * ```graphql
 * """The result of the user signup check."""
 * enum UserSignupCheckResult {
 *   """The entered email is already used."""
 *   EMAIL_ALREADY_USED
 * 
 *   """The entered FPH number is already used."""
 *   FPH_ALREADY_USED
 * 
 *   """Entered value is a FPH number but it is unknown."""
 *   FPH_UNKNOWN
 * 
 *   """The entered GLN is already used."""
 *   GLN_ALREADY_USED
 * 
 *   """Entered value is potentially a GLN but is not valid."""
 *   GLN_INVALID
 * 
 *   """Entered value is a valid GLN but it is unknown."""
 *   GLN_UNKNOWN
 * }
 * ```
 */
export const UserSignupCheckResult = Object.freeze({
  /** The entered email is already used. */
  EMAIL_ALREADY_USED: 'EMAIL_ALREADY_USED',
  /** The entered FPH number is already used. */
  FPH_ALREADY_USED: 'FPH_ALREADY_USED',
  /** Entered value is a FPH number but it is unknown. */
  FPH_UNKNOWN: 'FPH_UNKNOWN',
  /** The entered GLN is already used. */
  GLN_ALREADY_USED: 'GLN_ALREADY_USED',
  /** Entered value is potentially a GLN but is not valid. */
  GLN_INVALID: 'GLN_INVALID',
  /** Entered value is a valid GLN but it is unknown. */
  GLN_UNKNOWN: 'GLN_UNKNOWN'
});

/**
 * @example
 * ```graphql
 * enum WebformStateConditionTrigger {
 *   """Between"""
 *   BETWEEN
 * 
 *   """Checked"""
 *   CHECKED
 * 
 *   """Leer"""
 *   EMPTY
 * 
 *   """Filled"""
 *   FILLED
 * 
 *   """Größer als"""
 *   GREATER_THAN
 * 
 *   """Greater than/Equal to"""
 *   GREATER_THAN_EQUAL_TO
 * 
 *   """Kleiner als"""
 *   LESS_THAN
 * 
 *   """Less than/Equal to"""
 *   LESS_THAN_EQUAL_TO
 * 
 *   """Not between"""
 *   NOT_BETWEEN
 * 
 *   """Not Pattern"""
 *   NOT_PATTERN
 * 
 *   """Muster"""
 *   PATTERN
 * 
 *   """Unchecked"""
 *   UNCHECKED
 * 
 *   """Value is"""
 *   VALUE_IS
 * 
 *   """Value is not"""
 *   VALUE_IS_NOT
 * }
 * ```
 */
export const WebformStateConditionTrigger = Object.freeze({
  /** Between */
  BETWEEN: 'BETWEEN',
  /** Checked */
  CHECKED: 'CHECKED',
  /** Leer */
  EMPTY: 'EMPTY',
  /** Filled */
  FILLED: 'FILLED',
  /** Größer als */
  GREATER_THAN: 'GREATER_THAN',
  /** Greater than/Equal to */
  GREATER_THAN_EQUAL_TO: 'GREATER_THAN_EQUAL_TO',
  /** Kleiner als */
  LESS_THAN: 'LESS_THAN',
  /** Less than/Equal to */
  LESS_THAN_EQUAL_TO: 'LESS_THAN_EQUAL_TO',
  /** Not between */
  NOT_BETWEEN: 'NOT_BETWEEN',
  /** Not Pattern */
  NOT_PATTERN: 'NOT_PATTERN',
  /** Muster */
  PATTERN: 'PATTERN',
  /** Unchecked */
  UNCHECKED: 'UNCHECKED',
  /** Value is */
  VALUE_IS: 'VALUE_IS',
  /** Value is not */
  VALUE_IS_NOT: 'VALUE_IS_NOT'
});

/**
 * @example
 * ```graphql
 * enum WebformStateLogic {
 *   AND
 *   OR
 *   XOR
 * }
 * ```
 */
export const WebformStateLogic = Object.freeze({
  AND: 'AND',
  OR: 'OR',
  XOR: 'XOR'
});